import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config'; // ✅ SECURITY: For API_BASE_URL
import fetchWithAuth, { refreshAccessToken } from '../components/fetchWithAuth'; // ✅ SECURITY: For CSRF token injection
import { clearRequestCache } from '../utils/requestCache';

const AuthContext = createContext();

/**
 * ✅ SECURITY: AuthProvider with HttpOnly cookies
 * Tokens are stored securely in HttpOnly cookies by the server.
 * Frontend validates authentication state via API responses (401 = not authenticated).
 * We trust the server to validate tokens and manage sessions.
 */
// Helpers seguros para almacenamiento que no fallan en entornos sandboxed o crawlers
const safeSessionStorage = {
  getItem: (key) => {
    try { return typeof window !== 'undefined' && window.sessionStorage ? sessionStorage.getItem(key) : null; }
    catch { return null; }
  },
  setItem: (key, val) => {
    try { if (typeof window !== 'undefined' && window.sessionStorage) sessionStorage.setItem(key, val); }
    catch {}
  },
  removeItem: (key) => {
    try { if (typeof window !== 'undefined' && window.sessionStorage) sessionStorage.removeItem(key); }
    catch {}
  }
};

const safeLocalStorage = {
  getItem: (key) => {
    try { return typeof window !== 'undefined' && window.localStorage ? localStorage.getItem(key) : null; }
    catch { return null; }
  },
  setItem: (key, val) => {
    try { if (typeof window !== 'undefined' && window.localStorage) localStorage.setItem(key, val); }
    catch {}
  },
  removeItem: (key) => {
    try { if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem(key); }
    catch {}
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const cached = safeLocalStorage.getItem('auth_user_cache');
    if (cached) {
      try { return JSON.parse(cached); } catch { return null; }
    }
    return null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const localAuth = safeLocalStorage.getItem('auth_initialized');
    const sessionAuth = safeSessionStorage.getItem('auth_initialized');
    return localAuth === 'true' || sessionAuth === 'true';
  });

  const [isVerified, setIsVerified] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // ✅ SEGURIDAD & PERSISTENCIA: Validar y renovar sesión con cookies HttpOnly al montar
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        // TASK-131 F6: /api/auth/session responde 200 {user:null} a los visitantes anónimos (sin 401 en la
        // consola de la landing). Con accessToken se comporta igual que /api/auth/me.
        const pedirSesion = () => fetch(`${config.API_BASE_URL}/api/auth/session`, {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        });
        // Intentamos cargar datos del usuario con cookies HttpOnly
        let response = await pedirSesion();
        let userData = null;
        const leerUsuario = async () => {
          if (response.ok && response.status !== 304) {
            userData = await response.json();
          }
        };
        const esAnonimo = () => response.ok && response.status !== 304 && (!userData || userData.user === null);
        await leerUsuario();

        // TASK-140: la renovación usa la misma función que fetchWithAuth, que manda el token CSRF.
        // Un visitante que nunca inició sesión en este dispositivo no tiene refresh token:
        // no se intenta la renovación (evita un 401/403 por cada página pública).
        // La cookie refreshToken solo viaja a /api/auth/refresh, por eso el servidor no puede saber si hay
        // una sesión renovable: se decide con la marca local de sesión previa.
        const hadSession = safeLocalStorage.getItem('auth_initialized') === 'true'
          || safeSessionStorage.getItem('auth_initialized') === 'true';
        const renovarYReintentar = async () => {
          try {
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              response = await pedirSesion();
              userData = null;
              await leerUsuario();
            }
          } catch (_refreshErr) {
            console.warn('Silent refresh attempt failed:', _refreshErr);
          }
        };

        if (esAnonimo()) {
          if (hadSession) {
            await renovarYReintentar();
          }
          if (esAnonimo()) {
            setIsAuthenticated(false);
            setUser(null);
            if (hadSession) {
              safeLocalStorage.removeItem('auth_user_cache');
              safeLocalStorage.removeItem('auth_initialized');
              safeSessionStorage.removeItem('auth_initialized');
            }
            return;
          }
        }

        // ✅ PWA & Web Mobile: Si el accessToken expiró (401), intentamos un silent refresh automático
        if (response.status === 401 && hadSession) {
          await renovarYReintentar();
        }

        if (response.ok) {
          // 304 Not Modified: la sesión sigue activa (respuesta cacheada)
          if (response.status === 304) {
            setIsAuthenticated(true);
            safeLocalStorage.setItem('auth_initialized', 'true');
            safeSessionStorage.setItem('auth_initialized', 'true');
          } else {
            // Cargamos el usuario con su rol para que los guards de páginas funcionen
            // userData ya se leyó arriba salvo que el 401 se haya resuelto con el refresh
            const datosUsuario = userData || await response.json();
            setUser(datosUsuario);
            setIsAuthenticated(true);
            safeLocalStorage.setItem('auth_user_cache', JSON.stringify(datosUsuario));
            safeLocalStorage.setItem('auth_initialized', 'true');
            safeSessionStorage.setItem('auth_initialized', 'true');
          }
        } else if (response.status === 401) {
          // Sesión verdaderamente inválida o expirada
          setIsAuthenticated(false);
          setUser(null);
          safeLocalStorage.removeItem('auth_user_cache');
          safeLocalStorage.removeItem('auth_initialized');
          safeSessionStorage.removeItem('auth_initialized');
        } else if (response.status === 429) {
          // ⚠️ 429 Too Many Requests: NO desloguear al usuario por limite de solicitudes
          console.warn('Auth check hit 429 Rate Limit. Preservando sesión activa.');
          if (safeLocalStorage.getItem('auth_initialized') === 'true' || safeSessionStorage.getItem('auth_initialized') === 'true') {
            setIsAuthenticated(true);
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
          safeLocalStorage.removeItem('auth_user_cache');
          safeLocalStorage.removeItem('auth_initialized');
          safeSessionStorage.removeItem('auth_initialized');
        }
      } catch (error) {
        // Error de red / Modo Offline en PWA:
        // Si hay datos cacheados en localStorage, preservar la sesión del usuario para navegación offline
        console.log('Auth check network state (offline fallback):', error.message);
        const cachedUser = safeLocalStorage.getItem('auth_user_cache');
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
            setIsAuthenticated(true);
          } catch {
            setIsAuthenticated(false);
            setUser(null);
          }
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        setIsInitializing(false);
        safeLocalStorage.setItem('auth_check_done', 'true');
        safeSessionStorage.setItem('auth_check_done', 'true');
      }
    };

    checkAuthStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo se ejecuta al montar

  const login = useCallback(async (userData = null) => {
    // ✅ El servidor ya seteó las cookies HttpOnly con los tokens
    setIsAuthenticated(true);
    safeLocalStorage.setItem('auth_initialized', 'true');
    safeSessionStorage.setItem('auth_initialized', 'true');
    safeLocalStorage.removeItem('auth_check_done');
    safeSessionStorage.removeItem('auth_check_done');

    if (userData) {
      setUser(userData);
      safeLocalStorage.setItem('auth_user_cache', JSON.stringify(userData));
      safeLocalStorage.setItem('auth_check_done', 'true');
      safeSessionStorage.setItem('auth_check_done', 'true');
    }

    // Limpieza de estados persistentes de otros usuarios
    // (incluye la caché de peticiones GET en memoria, TASK-122)
    clearRequestCache();
    safeSessionStorage.removeItem('onboarding_wizard_data');
    safeSessionStorage.removeItem('onboarding_step');
  }, []);

  const logout = useCallback(async () => {
    try {
      // ✅ SECURITY: Llamamos al endpoint de logout para revocar los tokens en el servidor
      await fetchWithAuth(`${config.API_BASE_URL}/api/auth/logout`, {
        method: 'POST'
      }).catch(() => {}); // Si falla el logout remoto, igual limpiamos el estado local
    } finally {
      // Limpiamos todo el estado del cliente, incluida la caché de peticiones GET (TASK-122)
      clearRequestCache();
      setIsAuthenticated(false);
      setUser(null);
      setIsVerified(false);
      // Limpiamos todas las flags de sesión
      safeLocalStorage.removeItem('auth_user_cache');
      safeLocalStorage.removeItem('auth_initialized');
      safeLocalStorage.removeItem('auth_check_done');
      safeSessionStorage.removeItem('auth_initialized');
      safeSessionStorage.removeItem('auth_check_done');

      // Limpiar estados persistentes de sesión y evitar fuga de datos entre usuarios
      safeSessionStorage.removeItem('onboarding_wizard_data');
      safeSessionStorage.removeItem('onboarding_step');
      safeLocalStorage.removeItem('dashCtx_company');
      safeLocalStorage.removeItem('companyInfo');
    }
  }, []);

  const value = React.useMemo(() => ({
    isAuthenticated,
    user,
    isVerified,
    isInitializing,
    setIsVerified,
    setUser,
    login,
    logout
  }), [isAuthenticated, user, isVerified, isInitializing, login, logout]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};
